import { string, object } from 'prop-types';
import { useContext, useState, useEffect } from 'react';
import { Table, Switch } from 'antd';

import { HeadSEO } from 'components/base';
import { getLang, getIsMobile } from 'utils/helpers';
import { i18nContext } from 'context/i18n';
import Layout from 'components/layout';
import { IS_CAMPER, IS_NNORMAL } from 'utils/constants/system';

import styles from 'styles/Cookies.module.css';
import { getDomainValue } from 'utils/domain';

export default function CookiesPage({ data }) {
  const { t } = useContext(i18nContext);
  const isMobile = getIsMobile('992px');
  const [cookiesConsent, setCookiesConsent] = useState({
    2: false,
    3: false,
    4: false,
  });

  useEffect(() => {
    const cookiebotConsent = window?.Cookiebot?.consent;
    setCookiesConsent({
      2: cookiebotConsent?.preferences,
      3: cookiebotConsent?.statistics,
      4: cookiebotConsent?.marketing,
    });
  }, []);

  const columns = [
    {
      title: t('cookies.pagina', 'tabla.titulo.nombre', 'Name'),
      dataIndex: 'Name',
      key: 'Name',
      ellipsis: true,
    },
    {
      title: t('cookies.pagina', 'tabla.titulo.proveedor', 'Provider'),
      dataIndex: 'Provider',
      key: 'Provider',
      ellipsis: true,
      responsive: ['lg'],
    },
    {
      title: t('cookies.pagina', 'tabla.titulo.proposito', 'Purpose'),
      dataIndex: 'PurposeDescription',
      key: 'PurposeDescription',
      ellipsis: true,
      responsive: ['lg'],
    },
    {
      title: t('cookies.pagina', 'tabla.titulo.fuente.pais', 'Country Source'),
      dataIndex: 'InitiatorSourceDomainCountryName',
      key: 'InitiatorSourceDomainCountryName',
      ellipsis: true,
      responsive: ['lg'],
    },
    {
      title: t('cookies.pagina', 'tabla.titulo.expiracion', 'Expiration'),
      dataIndex: 'ExpireDescription',
      key: 'ExpireDescription',
      ellipsis: true,
    },
    {
      title: t('cookies.pagina', 'tabla.titulo.tipo', 'Type'),
      dataIndex: 'TrackerTypeName',
      key: 'TrackerTypeName',
      ellipsis: true,
      responsive: ['lg'],
    },
  ];
  const CATEGORIES = {
    1: {
      title: t('cookies.pagina', 'cookies.necesarias.titulo', 'Necessary'),
      description: t(
        'cookies',
        'cookies.necesarias.descripcion',
        'Necessary cookies help make a website usable by enabling basic functions like page navigation and access to secure areas of the website. The website cannot function properly without these cookies.',
      ),
    },
    2: {
      title: t('cookies.pagina', 'cookies.preferencias.titulo', 'Preferences'),
      description: t(
        'cookies',
        'cookies.preferencias.descripcion',
        'Preference cookies enable a website to remember information that changes the way the website behaves or looks, like your preferred language or the region that you are in.',
      ),
    },
    3: {
      title: t('cookies.pagina', 'cookies.estadisticas.titulo', 'Statistics'),
      description: t(
        'cookies',
        'cookies.estadisticas.descripcion',
        'Statistic cookies help website owners to understand how visitors interact with websites by collecting and reporting information anonymously.',
      ),
    },
    4: {
      title: t('cookies.pagina', 'cookies.marketing.titulo', 'Marketing'),
      description: t(
        'cookies.pagina',
        'cookies.marketing.descripcion',
        'Marketing cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user and thereby more valuable for publishers and third party advertisers.',
      ),
    },
    5: {
      title: t('cookies.pagina', 'cookies.sin.clasificar.titulo', 'Unclassified'),
      description: t('cookies.pagina', 'cookies.sin.clasificar.descripcion', 'Other cookies'),
    },
  };
  const CATEGORIES_MAP = {
    necessary: 1,
    preferences: 2,
    statistics: 3,
    marketing: 4,
    unclassified: 5,
  };
  const onChangeToggle = (checked, categoryNumber) => {
    const updatedCookiesConsent = JSON.parse(JSON.stringify(cookiesConsent));
    updatedCookiesConsent[categoryNumber] = checked;
    setCookiesConsent(updatedCookiesConsent);
    window.Cookiebot.submitCustomConsent(
      updatedCookiesConsent[CATEGORIES_MAP.preferences],
      updatedCookiesConsent[CATEGORIES_MAP.statistics],
      updatedCookiesConsent[CATEGORIES_MAP.marketing],
    );
  };
  return (
    <Layout>
      <div className={styles.main}>
        <HeadSEO
          title={getDomainValue({
            camper: 'Camper Shoes - Official Online Shop',
            nnormal: 'NNormal - Official Online Shop',
            camperlab: 'CamperLab - Official Online Shop',
          })}
          description={
            'Buy new shoes, boots, sneakers and accessories from the collection. Contemporary designs, original spirit.  Free shipping and 2 years warranty.'
          }
          pageName="CookiesPage"
        />

        <header className={styles.header}>
          <img
            src={getDomainValue({
              camper: '/assets-new/logo.jpg',
              nnormal: '/assets-new/logo-nnormal.svg',
              camperlab: '/assets-new/svg/camperlab/camperlab-logo.svg',
            })}
            alt={getDomainValue({
              camper: 'Camper logo',
              nnormal: 'Nnormal logo',
              camperlab: 'Camperlab logo',
            })}
            width={getDomainValue({
              camper: 150,
              nnormal: undefined,
              camperlab: undefined,
            })}
            height={76}
          />
          <h4>{t('cookies.pagina', 'titulo', 'Cookies Policy')}</h4>
        </header>
        <p className={styles.description}>
          {t(
            'cookies.pagina',
            'descripcion',
            'During your visit to the website, CAMPER may collect information using cookies. A cookie is a small text file that a server can place on users’ computer hard disk. It contains information about users’ website visits. Cookies can be used to compile specific information during their visit to the website. This information can help CAMPER to ensure that the website and the services are as closely adapted as possible to its users’ needs and preferences. In view of this, we would like to inform you that you may find the following types of cookies:',
          )}
        </p>

        {Object.keys(CATEGORIES).map((categoryType) => {
          const hasToggle = Number(categoryType) !== CATEGORIES_MAP.necessary && Number(categoryType) !== CATEGORIES_MAP.unclassified;
          const category = CATEGORIES[categoryType];
          const dataSource = data.cookies
            .filter((cookie) => cookie.Category === String(categoryType))
            .map((cookieContent, index) => ({
              ...cookieContent,
              key: index,
            }));
          return (
            <div className={styles.categoryDiv} key={categoryType}>
              <div className={styles.categoryWrapper}>
                <span className={styles.categoryTitle}>{category.title}</span>
                {hasToggle ?
                  <Switch
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    checked={cookiesConsent[Number(categoryType)]}
                    onChange={(checked) => onChangeToggle(checked, Number(categoryType))}
                  />
                : null}
              </div>
              <p>{category.description}</p>
              {!isMobile ?
                <Table dataSource={dataSource} columns={columns} pagination />
              : <Table
                  dataSource={dataSource}
                  columns={columns}
                  pagination
                  expandable={{
                    // eslint-disable-next-line react/display-name
                    expandedRowRender: (record) => (
                      <ul className={styles.subrowUl} key={record.key} style={{ margin: 0 }}>
                        {columns
                          .filter((col) => col.responsive)
                          .map((colFiltered, i) => (
                            <li key={`li.${record.key + i}`}>
                              <span className={styles.subrowTitle}>{colFiltered.title}</span>
                              <span>{record[colFiltered.key]}</span>
                            </li>
                          ))}
                      </ul>
                    ),
                  }}
                />
              }
            </div>
          );
        })}
      </div>
    </Layout>
  );
}

CookiesPage.propTypes = {
  locale: string,
  data: object,
};

CookiesPage.i18nPage = 'CookiesPage';

export async function getServerSideProps(context) {
  const locale = ['default', 'int'].includes(context.locale) ? 'es_ES' : context.locale;
  const lang = getLang(locale);
  const url =
    IS_NNORMAL ?
      `https://www.nnormal.com/html/legal/cookies/cookies-declaration/cookies-declaration-${lang}.json`
    : `https://www.camper.com/html/legal/cookies/cookies-declaration/cookies-declaration-${lang}.json`;
  const res = await fetch(url);
  const data = await res.json();

  return {
    props: {
      locale,
      data,
    },
  };
}
